import { useMemo, useRef } from "react";
import { diffWordsWithSpace, type Change } from "diff";

interface DiffViewerProps {
  oldTitle: string;
  newTitle: string;
  oldText: string;
  newText: string;
}

export function DiffViewer({ oldTitle, newTitle, oldText, newText }: DiffViewerProps) {
  const leftPaneRef = useRef<HTMLDivElement>(null);
  const rightPaneRef = useRef<HTMLDivElement>(null);
  const isSyncingLeft = useRef(false);
  const isSyncingRight = useRef(false);

  const changes = useMemo<Change[]>(() => {
    return diffWordsWithSpace(oldText || "", newText || "");
  }, [oldText, newText]);

  const handleLeftScroll = () => {
    if (isSyncingLeft.current) {
      isSyncingLeft.current = false;
      return;
    }
    if (leftPaneRef.current && rightPaneRef.current) {
      isSyncingRight.current = true;
      rightPaneRef.current.scrollTop = leftPaneRef.current.scrollTop;
    }
  };

  const handleRightScroll = () => {
    if (isSyncingRight.current) {
      isSyncingRight.current = false;
      return;
    }
    if (leftPaneRef.current && rightPaneRef.current) {
      isSyncingLeft.current = true;
      leftPaneRef.current.scrollTop = rightPaneRef.current.scrollTop;
    }
  };

  const hasOldContent = Boolean(oldText?.trim());
  const hasNewContent = Boolean(newText?.trim());

  return (
    <div className="w-full rounded-xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 overflow-hidden shadow-2xs">
      <div className="grid grid-cols-2 divide-x divide-stone-200 dark:divide-stone-800 w-full">
        {/* Left Column (50% Prior Revision) */}
        <div className="w-full min-w-0 flex flex-col">
          <div className="px-3.5 py-2.5 border-b border-stone-200 dark:border-stone-800 bg-stone-50/75 dark:bg-stone-800/50 flex items-center justify-between gap-2 min-w-0">
            <span className="text-xs font-semibold text-stone-700 dark:text-stone-300 truncate" title={oldTitle}>
              {oldTitle}
            </span>
            <span className="text-[10px] font-medium text-stone-500 dark:text-stone-400 shrink-0 whitespace-nowrap px-1.5 py-0.5 rounded bg-stone-200/50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700/60">
              Prior Revision
            </span>
          </div>

          <div
            ref={leftPaneRef}
            onScroll={handleLeftScroll}
            className="p-3.5 bg-stone-50/30 dark:bg-stone-950/40 text-xs font-mono leading-relaxed whitespace-pre-wrap max-h-80 overflow-y-auto flex-1 select-text [scrollbar-gutter:stable]"
          >
            {!hasOldContent ? (
              <span className="text-stone-400 dark:text-stone-500 italic">(Empty baseline instruction)</span>
            ) : (
              changes.map((part, i) => {
                if (part.added) return null;
                if (part.removed) {
                  return (
                    <span
                      key={i}
                      className="bg-rose-100/80 dark:bg-rose-950/70 text-rose-800 dark:text-rose-200 line-through decoration-rose-400 dark:decoration-rose-600 px-0.5 rounded-xs"
                    >
                      {part.value}
                    </span>
                  );
                }
                return (
                  <span key={i} className="text-stone-700 dark:text-stone-300">
                    {part.value}
                  </span>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column (50% Selected Revision) */}
        <div className="w-full min-w-0 flex flex-col">
          <div className="px-3.5 py-2.5 border-b border-stone-200 dark:border-stone-800 bg-stone-50/75 dark:bg-stone-800/50 flex items-center justify-between gap-2 min-w-0">
            <span className="text-xs font-semibold text-stone-700 dark:text-stone-300 truncate" title={newTitle}>
              {newTitle}
            </span>
            <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300 shrink-0 whitespace-nowrap px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800">
              Selected Revision
            </span>
          </div>

          <div
            ref={rightPaneRef}
            onScroll={handleRightScroll}
            className="p-3.5 bg-white dark:bg-stone-900 text-xs font-mono leading-relaxed whitespace-pre-wrap max-h-80 overflow-y-auto flex-1 select-text [scrollbar-gutter:stable]"
          >
            {!hasNewContent ? (
              <span className="text-stone-400 dark:text-stone-500 italic">(Empty proposed instruction)</span>
            ) : (
              changes.map((part, i) => {
                if (part.removed) return null;
                if (part.added) {
                  return (
                    <span
                      key={i}
                      className="bg-emerald-100/80 dark:bg-emerald-950/70 text-emerald-900 dark:text-emerald-200 font-medium px-0.5 rounded-xs"
                    >
                      {part.value}
                    </span>
                  );
                }
                return (
                  <span key={i} className="text-stone-700 dark:text-stone-300">
                    {part.value}
                  </span>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
